// src/game/tutorialMode.ts — Eğitim modu denetleyicisi (docs/TUTORIAL.md §1 akış · §2 kukla · §5 geri bildirim · §6 kancalar).
// Bu dosya PARÇALARI BAĞLAR, kendi kuralını yazmaz: ders mantığı game/tutorial.ts (TutorialRunner), panel view/tutorialHud.ts,
// kukla davranışı sim/bot/dummy.ts. Metinlerin tamamı LESSONS'tan gelir (belge birebir); burada ders metni ÜRETİLMEZ.
//
// NEDEN sim'e dokunulmuyor: "ölüm yok" ve "kukla düşmez" kuralları sim dosyalarına bayrak eklemek yerine `world.step()`
// SONRASI çalışan saf yardımcılarla uygulanıyor (fighter alanları + world.events.push). Böylece normal maç kodu, determinizm
// testleri ve tuning sayıları hiç değişmiyor; eğitim kapalıyken tek bir dal bile çalışmıyor.
//   • Oyuncu: y < −4'te YAKALANIR — durumu Falling'e alınır (collision.ts kill plane'i Falling'i atlar → Ev.Fell, can kaybı,
//     "RING OUT" bandı, kamera sarsıntısı ve slow-mo hiç oluşmaz), 30 t sonra EN YAKIN spawn'da Ev.Respawn ile geri gelir.
//   • Kukla: `keepDummyAlive` (koşulsuz kenar freni) + her tick can tazeleme (elenip maçı bitirmesin). `kenara-at` dersinde
//     koruma kapalı; düşerse sim'in kendi yeniden doğuşu onu 90 t'de geri getirir.
//   • Sahne düzeni (oyun testi bulguları): `upkeepAttacks` 'saldırır' dersinde kilitlenmeyi açar (kukla menzil dışına
//     düşünce yeniden önüne alınır), `upkeepLying` 10./11. derste kuklayı yatar tutar, `gatherToCenter` ikiliyi kenardan
//     merkeze doğru toplar. Üçü de yalnız fighter alanlarını yazar; sim/ ve config/ dosyaları değişmez.
// Panel bir ARAYÜZ (TutorialPanel) üzerinden konuşur: DOM'suz testte sahte panel verilebilir, tarayıcıda TutorialHud kurulur.
import { Ev, FState } from '../core/types.ts';
import type { ArenaDef, Fighter, SimEvent, SimWorld, Vec3 } from '../core/types.ts';
import { NO_ID, NO_TICK } from '../config/constants.ts';
import { yawOf } from '../core/vec3.ts';
import { ARENA } from '../config/arena.ts';
import { T } from '../config/tuning.ts';
import { placeFighter, setState } from '../sim/fighter.ts';
import { enterLying } from '../sim/knockdown.ts';
import { createDummy, dummyUntouchedFor, faceDummy, keepDummyAlive, resetDummy, type DummyBot } from '../sim/bot/dummy.ts';
import { pickPadIndex } from '../input/gamepad.ts';
import { TutorialHud, type TutorialLessonView, type TutorialProgress } from '../view/tutorialHud.ts';
import type { UiSoundKind } from '../view/audio.ts';
import { LESSONS, TutorialRunner, fillInput, type TutorialCue, type TutorialLesson } from './tutorial.ts';

/* ------------------------------------------------------------- sabitler (yalnız eğitim; sim sayısı değil) */

export const TUT_MODE = {
  /** TUTORIAL §1: oyuncu bu yüksekliğin altına düşerse yakalanır (kill plane −8'e hiç ulaşmaz). */
  catchY: -4,
  /** Yakalama → geri gelme (30 t = 0.5 s). */
  recoverTicks: 30,
  /** Kukla bu kadar tick vurulmazsa tazelenir (TUTORIAL §1: 360 t = 6 s). */
  dummyResetTicks: 360,
  /** Ders sonu bilgisi olan derslerde geçiş beklemesi (t): 90 t'lik zorunlu bekleme okumaya yetmiyor (bkz. `holdForNote`). */
  noteHoldTicks: 210,
  /** Panelde ders sonu bilgisinin ekranda kalma süresi (s). */
  noteSec: 3.5,
  /** "Düştün, hemen geri geldin." satırının süresi (s). */
  fallNoteSec: 2.5,
  /** Silah dersleri: iki doğma denemesi arası (t) — havadaki/alınmakta olan kılıç ikinci kez doğmasın. */
  itemRetryTicks: 90,
  /** Kılıç oyuncunun kaç u ötesine düşsün. */
  itemDist: 1.6,
  /** Kılıcın kenara en az uzaklığı (u): doğar doğmaz aşağı yuvarlanmasın. */
  itemEdgeKeep: 1.2,
  /** Kılıcın kuklaya en az uzaklığı (u): tutma menzili 1.6 u — kılıcın üstündeki oyuncu B'ye basınca KUKLAYI tutuyordu. */
  itemAvoid: 2.6,
  /**
   * 'saldırır' dersleri: kukla oyuncudan bundan uzaktaysa önüne alınır (bkz. `parkDummy`).
   * DUMMY_ATTACK_RANGE'in (2.0 u) ALTINDA olmak ZORUNDA: eskiden 2.2 idi ve arada 2.0–2.2 u'luk bir ÖLÜ BANT vardı —
   * kukla vurmuyor (menzil dışı), park da etmiyordu (yeterince uzak değil). Oyun testinde oyuncu 2.1 u'da durunca
   * blok/savuşturma dersi 10 s boyunca tek olay üretmeden 0/3'te kaldı.
   */
  parkFrom: 1.8,
  /** Alınırken bırakılacak mesafe (u): kuklanın koruduğu 1.2–1.6 u bandının içi. */
  parkDist: 1.4,
  /** 'saldırır' dersinde bu kadar tick hiç isabet/blok/savuşturma olmazsa kukla YENİDEN önüne alınır (bkz. `upkeepAttacks`).
   *  180 t (3 s) > kuklanın 150 t'lik vuruş ritmi → normal tempoda hiç tetiklenmez, yalnız kilitlenmede. */
  stallTicks: 180,
  /** Kenar toplama (bkz. `gatherToCenter`): kenara bundan yakın olan taraf merkeze doğru kaydırılır (u). */
  edgeKeep: 2,
  /** Kenar toplamanın tick başına en çok kaydırdığı mesafe (u): 0.1 u/t = 6 u/s ≈ yürüme hızı — ışınlanma değil kısa kayma. */
  edgePull: 0.1,
} as const;

/** Kontrol kilidi "sonsuz" (match.ts'teki FAR_FUTURE ile aynı anlam). */
const FAR_FUTURE = 0x3fffffff;
/** Silah derslerinde doğan prop (TUTORIAL §6 kanca 4). */
const SWORD = 'sword_1handed';
/** Kuklanın düşme korumasının KAPALI olduğu tek ders (TUTORIAL §1). */
const NO_EDGE_LOCK_LESSON = 'kenara-at';
/** Kılıç isteyen dersler (TUTORIAL §6 kanca 4). */
const WEAPON_LESSONS: ReadonlySet<string> = new Set(['silah-al', 'silahla-vur', 'silah-firlat']);
/** Kuklanın YERDE başlaması gereken dersler (10. "Yerdekini Kaldır" ve 11. "Taşı ve Fırlat"); bkz. `upkeepLying`. */
const LYING_LESSONS: ReadonlySet<string> = new Set(['yerden-kaldir', 'tasi-ve-firlat']);
/** Kilitlenme nöbetçisinin "temas" saydığı olaylar (bkz. `upkeepAttacks`). */
const CONTACT_EVENTS: ReadonlySet<Ev> = new Set([Ev.Hit, Ev.Blocked, Ev.Parry]);
/** `resetDummy` aslında genel bir "dövüşçüyü tazele"dir (hasar %0, sersemlik 0, guard dolu, seri/isabet izleri temiz);
 *  adı sim'de yalnız kukla için kullanıldığından öyle kalmış. Eğitimde ders başında OYUNCUYA da uygulanıyor → okunur ad.
 *  Sim dosyası değişmiyor: yalnız yeniden adlandırma. */
const refreshFighter = resetDummy;
/** Pad kısayolları (TUTORIAL §1): R2 sonraki ders, L2 önceki. Oyunda kullanılmıyorlar → çakışma yok. */
const PAD_R2 = 7, PAD_L2 = 6;
/** Klavye kısayolları (TUTORIAL §1). KEY_BUTTONS'da yoklar → oyun girdisiyle çakışmaz. */
const KEY_NEXT = 'KeyN', KEY_PREV = 'KeyB', KEY_RESTART = 'KeyR';

/** Son ders (serbest antrenman) küçük panelinde gösterilen özet tuş listesi. NEDEN burada: ders verisinde tuş yok
 *  (belge "panel küçülüp yalnız tuş listesini gösterir" diyor) — bunlar metin değil, controls.ts'in rozet karşılıkları. */
const FREE_KEYS = [
  { kb: ['W', 'A', 'S', 'D'], pad: ['Sol çubuk'] },
  { kb: ['Space'], pad: ['A'], label: 'zıpla' },
  { kb: ['J'], pad: ['X'], label: 'hafif' },
  { kb: ['K'], pad: ['Y'], label: 'ağır' },
  { kb: ['L'], pad: ['B'], label: 'tut' },
  { kb: ['Q'], pad: ['L1'], label: 'blok' },
  { kb: ['C'], pad: ['R1'], label: 'sıyrıl' },
  { kb: ['T'], pad: ['D-pad ↓'], label: 'gösteriş' },
] as const;

/* ------------------------------------------------------------- arayüzler */

// Panel sözleşmesi tek yerden okunabilsin (denetleyiciyi kullanan test/araç view'e ayrıca bakmasın).
export type { TutorialLessonView, TutorialProgress } from '../view/tutorialHud.ts';

/** Panelin denetleyiciye bakan yüzü (view/tutorialHud.ts TutorialHud bunu yapısal olarak karşılar). */
export interface TutorialPanel {
  show(lesson: TutorialLessonView, progress?: TutorialProgress): void;
  setProgress(done: number, need: number): void;
  showHint(text: string): void;
  clearHint(): void;
  note(text: string, sec?: number): void;
  celebrate(text: string): void;
  hide(): void;
  dispose(): void;
}

export interface TutorialModeOptions {
  world: SimWorld;
  playerId: number;
  dummyId: number;
  /** Kukla botu (createDummy); ders değişiminde modu buradan değişir. */
  dummy: DummyBot;
  /** Yalnız `ui(kind)` kullanılır (TUTORIAL §5 ses tablosu). */
  audio: { ui(kind: UiSoundKind): void };
  /** Panelin ekleneceği kap (#hud-root); `panel` verilirse kullanılmaz. */
  container?: HTMLElement;
  /** DOM'suz test için sahte panel; verilmezse TutorialHud kurulur. */
  panel?: TutorialPanel;
  /** Duraklatmada kısayollar çalışmasın (Game.paused). */
  isBlocked?: () => boolean;
  /** Testler için ders listesi. */
  lessons?: readonly TutorialLesson[];
}

/** Eğitim arenası: eşya doğma noktaları BOŞ → items.ts'in kendi zamanlayıcısı hiç doğuramaz (TUTORIAL §6 "eşya doğumu kapalı").
 *  NEDEN kopya: ARENA modül sabiti; platformlar/spawn'lar aynen paylaşılır, yalnız itemSpawns boşaltılır (sim değişmez). */
export function tutorialArena(): ArenaDef {
  return { ...ARENA, itemSpawns: [] };
}

/* ------------------------------------------------------------- denetleyici */

export class TutorialMode {
  readonly runner: TutorialRunner;
  readonly panel: TutorialPanel;
  private readonly world: SimWorld;
  private readonly playerId: number;
  private readonly dummyId: number;
  private readonly dummy: DummyBot;
  private readonly audio: { ui(kind: UiSoundKind): void };
  private readonly blocked: () => boolean;

  /** Ders değişimi algısı: index ya da başlangıç tick'i değişince ders yeniden kurulur (R ve düşüş sıfırlaması dâhil). */
  private lastIndex = -1;
  private lastStart = -1;
  /** Panelde yazılı ipucu (aynı metni iki kez yazmamak için). */
  private hintShown = '';
  /** Ders değişiminden SONRA yazılacak kısa bilgi satırı (panel show() ipucu temizler → sıraya alınır). */
  private pendingNote = '';
  /** Oyuncu yakalandı: bu tick'te geri gelir (NO_TICK = düşmüyor). */
  private recoverAt = NO_TICK;
  /** Bir sonraki kılıç doğma denemesinin en erken tick'i. */
  private itemAt = 0;
  /** Kuklanın "evi" (düşerse ışınlanacağı nokta): ders başında bulunduğu yer. */
  private homeX = 0;
  private homeZ = 0;
  /** Son isabet/blok/savuşturma tick'i — 'saldırır' dersinde kilitlenme nöbetçisi (bkz. `upkeepAttacks`). */
  private lastContactTick = 0;
  /** Yatar ders: kukla yerde TUTULUYOR (yatma süresi her tick tazeleniyor). */
  private holdLying = false;
  /** Pad kenarı (R2/L2). */
  private prevPad = 0;
  private disposed = false;

  constructor(opts: TutorialModeOptions) {
    this.world = opts.world;
    this.playerId = opts.playerId;
    this.dummyId = opts.dummyId;
    this.dummy = opts.dummy;
    this.audio = opts.audio;
    this.blocked = opts.isBlocked ?? (() => false);
    this.runner = new TutorialRunner({
      playerId: opts.playerId, dummyId: opts.dummyId, lessons: opts.lessons,
      onCue: (c) => this.onCue(c),
    });
    // Yer tutucu doldurucu panele BURADA bağlanır: sözcük tablosu ders verisinin yanında (fillInput), bağlı girdiyi
    // ise yalnız panel biliyor — view game'den içe aktaramayacağı için işlev dışarıdan verilir.
    this.panel = opts.panel ?? new TutorialHud(opts.container, fillInput);
    const d = this.world.fighters[this.dummyId];
    this.homeX = d ? d.pos.x : 0;
    this.homeZ = d ? d.pos.z : 0;
    this.applyLesson();
  }

  /* ------------------------------------------------------- oyun döngüsü kancaları */

  /**
   * `world.step()` SONRASI, olaylar tüketilmeden ÖNCE (game.ts): koruma kuralları ve oyuncunun yakalanması burada işler —
   * ürettiği `Ev.Respawn` aynı tick'te panele/sese/vfx'e ulaşsın diye.
   */
  beforePresent(): void {
    this.guardDummy();
    this.guardPlayer();
    this.upkeepLying();
    this.upkeepAttacks();
    this.gatherToCenter();
    this.upkeepWeapon();
  }

  /** Her sim olayı (game.ts consumeEvents). */
  onEvent(e: SimEvent, world: SimWorld): void {
    // Kilitlenme nöbetçisi: kukla ile oyuncu arasında hâlâ temas var mı (bkz. `upkeepAttacks`).
    if (CONTACT_EVENTS.has(e.type)) this.lastContactTick = e.tick;
    this.runner.onEvent(e, world);
  }

  /** Olaylar tüketildikten SONRA (aynı tick): ders ilerlemesi, ders değişimi, panel ve kılıç bakımı. */
  afterPresent(): void {
    const world = this.world;
    if (!this.holdForNote(world.tick)) this.runner.onTick(world);
    this.syncLesson();
    this.syncPanel(world.tick);
  }

  /** Her render karesi: pad kısayolları (R2 / L2). Klavye olay tabanlı, burada okunmaz. */
  pollShortcuts(): void {
    if (this.disposed) return;
    let cur = 0;
    if (typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function') {
      // getGamepads() kare başına dizi tahsis eder → BİR kez okunur (yuva seçimi de aynı dizi üzerinden).
      const pads = navigator.getGamepads();
      const idx = pickPadIndex(pads, 0);
      const pad = idx >= 0 ? pads[idx] : null;
      if (pad) {
        const r2 = pad.buttons[PAD_R2], l2 = pad.buttons[PAD_L2];
        if (r2 && (r2.pressed || r2.value > 0.5)) cur |= 1;
        if (l2 && (l2.pressed || l2.value > 0.5)) cur |= 2;
      }
    }
    const edge = cur & ~this.prevPad;
    this.prevPad = cur;
    if (this.blocked()) return;
    if (edge & 1) this.next();
    else if (edge & 2) this.prev();
  }

  /* ------------------------------------------------------- kısayollar (TUTORIAL §1) */

  /** `N` / R2 — sonraki ders (atlama da aynı; sesi 'start'). */
  next(): void { this.runner.skip(this.world.tick); this.syncLesson(); }
  /** `B` / L2 — önceki ders. */
  prev(): void { this.runner.prev(this.world.tick); this.syncLesson(); }
  /** `R` — dersi baştan al. */
  restart(): void { this.runner.restart(this.world.tick); this.syncLesson(); }

  /** Klavye kancası (Game bağlar; N/B/R). Dönüş: tuş tüketildi mi. */
  handleKey(code: string): boolean {
    if (this.disposed || this.blocked()) return false;
    if (code === KEY_NEXT) { this.next(); return true; }
    if (code === KEY_PREV) { this.prev(); return true; }
    if (code === KEY_RESTART) { this.restart(); return true; }
    return false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.panel.dispose();
  }

  /* ------------------------------------------------------- ses / kutlama (TUTORIAL §5) */

  private onCue(cue: TutorialCue): void {
    switch (cue) {
      case 'progress': this.audio.ui('select'); return;         // her doğru tekrar
      case 'complete': this.audio.ui('start'); this.celebrate(); return;
      case 'advance': this.audio.ui('move'); return;            // kendiliğinden geçiş
      case 'skip': this.audio.ui('start'); return;              // N ile atlama
      case 'back': this.audio.ui('back'); return;               // B / R
      default: return;
    }
  }

  /** Ders tamamlandı: kısa övgü + (varsa) ders sonu bilgisi. */
  private celebrate(): void {
    this.panel.celebrate(this.runner.praise);
    const note = this.runner.lesson.note;
    if (note) { this.panel.note(note, TUT_MODE.noteSec); this.hintShown = ''; }
  }

  /**
   * Ders sonu bilgisi olan derslerde geçişi biraz geciktirir. NEDEN: belgedeki 90 t (1.5 s) bir cümleyi okumaya yetmiyor
   * ve bilgi panel değişince siliniyordu; TutorialRunner'a dokunmadan (testleri aynen geçerli) onTick çağrısı geciktirilir.
   * Hedefi dolduran ders zaten "done" durumunda — sim akmaya devam eder, oyuncu bekletilmez.
   */
  private holdForNote(tick: number): boolean {
    if (this.runner.state !== 'done' || this.runner.doneTick === NO_TICK) return false;
    if (!this.runner.lesson.note) return false;
    return tick - this.runner.doneTick < TUT_MODE.noteHoldTicks;
  }

  /* ------------------------------------------------------- ders kurulumu */

  /** Ders değişti mi (index ya da yeniden başlatma) → kur. */
  private syncLesson(): void {
    if (this.runner.index === this.lastIndex && this.runner.startTick === this.lastStart) return;
    this.applyLesson();
  }

  /** Dersi kurar: kukla modu + tazeleme, panel içeriği, kılıç bakımı (TUTORIAL §1/§2). */
  private applyLesson(): void {
    const world = this.world, tick = world.tick, lesson = this.runner.lesson;
    this.lastIndex = this.runner.index;
    this.lastStart = this.runner.startTick;
    this.hintShown = '';
    // kukla: ders modunda, oyuncuyu hedefleyerek, ritmi sıfırdan; hasarı/sersemliği %0
    this.dummy.setTarget(this.playerId);
    this.dummy.setMode(this.runner.dummyMode, tick);
    refreshFighter(world, this.dummyId);
    // OYUNCU da temiz başlar: yüzde ders boyunca birikiyordu (oyun testi: serbest antrenmana %51 ile giriliyor) ve
    // "vuruşlar canını yakmaz" diyen blok dersinde çocuk kendi sayısının büyüdüğünü görüyordu. Konuma/duruma dokunulmaz.
    refreshFighter(world, this.playerId);
    this.lastContactTick = tick;
    // 10./11. ders kuklanın YERDE olmasını ister; yatırma bir sonraki `beforePresent`te (olaylar tüketilmeden önce) olur.
    this.holdLying = LYING_LESSONS.has(lesson.id);
    if (this.runner.dummyMode === 'attacks') this.parkDummy();
    const d = world.fighters[this.dummyId];
    if (d && world.query.isInside(d.pos.x, d.pos.z)) { this.homeX = d.pos.x; this.homeZ = d.pos.z; }
    // Kılıç bir sonraki `beforePresent`te doğar: doğma olayı olaylar tüketilmeden ÖNCE üretilmeli (ses/vfx görsün).
    this.itemAt = tick;
    this.panel.show(lessonView(lesson, this.runner.lessonNo, this.runner.total), this.runner.progress);
    if (this.pendingNote) { this.panel.note(this.pendingNote, TUT_MODE.fallNoteSec); this.pendingNote = ''; }
  }

  /**
   * 'saldırır' derslerinde (sıyrılma / blok / savuşturma) kuklayı oyuncunun önüne alır — ders başında ve kilitlenme
   * nöbetçisiyle ders ORTASINDA da (bkz. `upkeepAttacks`), yalnız uzaktaysa. NEDEN: kukla kovalamaz (TUTORIAL §2) ve
   * 2 u'dan uzaktakine vurmaz; sıyrılıp uzaklaşan oyuncu "3 vuruşu blokla" derste hiçbir şey olmadan bekler kalırdı.
   * Yer arena MERKEZİNE doğru seçilir: ikisi de kenardan uzak dursun. Kukla oyuncuya döner; hasarı zaten temiz.
   */
  private parkDummy(): void {
    const world = this.world, tick = world.tick;
    const p = world.fighters[this.playerId], d = world.fighters[this.dummyId];
    if (!p || !d) return;
    if (this.recoverAt !== NO_TICK) return;                          // oyuncu düşüyor: geri gelmesini bekle
    if (d.grabbedBy !== NO_ID || p.grabTarget !== NO_ID) return;     // tutuş sürüyor: yerinden oynatma
    if (Math.hypot(d.pos.x - p.pos.x, d.pos.z - p.pos.z) <= TUT_MODE.parkFrom) return;
    let dx = world.arena.center.x - p.pos.x, dz = world.arena.center.z - p.pos.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 0.5) { dx = Math.sin(p.yaw); dz = Math.cos(p.yaw); } else { dx /= len; dz /= len; }
    const x = p.pos.x + dx * TUT_MODE.parkDist, z = p.pos.z + dz * TUT_MODE.parkDist;
    if (!world.query.isInside(x, z)) return;
    const gy = world.query.groundY(x, z, p.pos.y + 2);
    if (Number.isNaN(gy)) return;   // oyuncu havada/platform hizasının altında: zemin bulunamadı, kuklayı boşluğa koyma
    placeFighter(d, { x, y: gy, z }, yawOf(p.pos.x - x, p.pos.z - z), tick);
    setState(d, FState.Idle, tick);
  }

  /**
   * BLOCKING bulgunun ikinci yarısı: ders ORTASINDA kilitlenme. Kukla 2 u'dan uzağa vurmadığı ve kovalamadığı için
   * oyuncu geri çekilince 'saldırır' dersi tek olay üretmeden 0/3'te kalabiliyordu. Nöbetçi: `stallTicks` (3 s) boyunca
   * hiç isabet/blok/savuşturma gelmediyse kukla yeniden önüne alınır. Kukla zaten yakınsa `parkDummy` dokunmaz →
   * "vurulmuyorum ama duruyoruz" hâlinde gereksiz ışınlanma olmaz.
   */
  private upkeepAttacks(): void {
    if (this.runner.dummyMode !== 'attacks') return;
    const tick = this.world.tick;
    if (tick - this.lastContactTick < TUT_MODE.stallTicks) return;
    this.lastContactTick = tick;
    this.parkDummy();
  }

  /**
   * 10. ("Yerdekini Kaldır") ve 11. ("Taşı ve Fırlat") ders kuklanın YERDE olmasını ister; `applyLesson` ise her ders
   * başında yalnız hasarı temizliyordu → kukla AYAKTA başlıyor, dersin hedefi hiç denenemiyordu. Yere serilse bile
   * yatma süresi 84 t (1.4 s): çocuk yanına varmadan kalkıyor.
   * Çözüm oyun katmanında (sim değişmez): ders başında bir kez `enterLying` — `beforePresent` içinde çağrıldığı için
   * `Ev.Lying` aynı tick sunulur (animasyon/ses görür) — sonra her tick `lyingUntil` tazelenir, yani kukla KENDİLİĞİNDEN
   * kalkmaz. Oyuncu onu kaldırınca (grabbedBy) tutma askıya alınır.
   * NEDEN kaldırmadan sonra da sürüyor: 11. ders İKİ fırlatma istiyor; kukla ilk fırlatmadan sonra ayağa kalksaydı
   * ikinci tekrar imkânsız olurdu (o derste yere sermeyi öğreten bir adım yok). Hedef dolunca tutma biter: kukla
   * kutlama sırasında normal şekilde kalkar.
   * "Kaldırma gerçekleşti" işaretini sim'in kendi kaydından okuyoruz (`grabbedBy`), olaydan değil: sim AYAKTAKİNİ omuza
   * alırken `Ev.PickUpFighter`, YATANI kaldırırken `Ev.Grab` üretiyor — alan ikisinde de aynı tick'te yazılıyor.
   */
  private upkeepLying(): void {
    if (!this.holdLying || this.runner.state !== 'active') return;
    const world = this.world, tick = world.tick;
    const f = world.fighters[this.dummyId];
    if (!f) return;
    if (f.grabbedBy !== NO_ID) return;                                     // oyuncunun elinde/omzunda: tutma askıda
    if (f.state === FState.Lying || f.state === FState.Crawl) {
      f.lyingUntil = tick + T.KNOCKDOWN.lyingTicks;                        // süreyi tazele → kendi kendine kalkmaz
      f.stateUntil = f.lyingUntil;
      return;
    }
    // Savrulma/çakılma zincirinin ortasına karışma: sim kendi Lying'ine zaten indirir (fırlatılan kukla böyle geri yatar).
    if (!f.grounded) return;
    const free = f.state === FState.Idle || f.state === FState.Move || f.state === FState.Block || f.state === FState.GetUp;
    if (free) enterLying(world, f, T.KNOCKDOWN.lyingTicks, false);
  }

  /**
   * MAJOR bulgu: dersler ilerledikçe ikili arena KENARINA sıkışıyordu (oyun testi 8–18. dersleri köşede oynadı) —
   * savrulma dışa itiyor, `keepDummyAlive` yalnız DÜŞMEYİ engelliyor, `parkDummy` yalnız 'saldırır' derslerinde çalışıyor.
   * Burada kural GENEL: kenara `edgeKeep` (2 u) yaklaşan taraf merkeze doğru çekilir. `kenara-at` dersinde KAPALI —
   * orada kenar dersin kendisidir.
   * NEDEN ışınlama değil kayma: ders ortasında ani yer değiştirme çocuğun elinden kontrolü alır. Bunun yerine tick başına
   * en çok `edgePull` (0.1 u/tick ≈ 6 u/s) kaydırılır: ekranda kısa bir kayma görünür ve yarım saniyede biter.
   *
   * GİRDİYE GÖRE FARKLI HİSSEDİLİR (oyun testi ölçtü): PAD'de ısrar eden oyuncu koşarak (6.5 u/s) yamacı aşabilir —
   * kafes değil. KLAVYEDE ise kenar alarm bölgesinde `keyEdgeBrakeOn` hareketi 0.55'e kırptığı için hız 2.8–3.5 u/s'ye
   * düşer, yani bu kaymanın altında kalır: halka klavye oyuncusu için sert bir sınır gibi davranır (kenarda gidip gelme).
   * Eğitimde istenen zaten bu (çocuk kenara sıkışmasın); `kenara-at` dersi muaf olduğu için platformdan atma yine öğrenilebiliyor.
   * NEDEN doğuş koruması vermiyoruz: blok/savuşturma derslerinde gelen vuruşu yutar, ders ilerlemezdi. Onun yerine kayma
   * yalnız YERDEYKEN uygulanır (zıplama/uçan tekme kavisi bozulmasın; düşen oyuncuyu zaten `guardPlayer` yakalıyor).
   */
  private gatherToCenter(): void {
    if (this.runner.lesson.id === NO_EDGE_LOCK_LESSON) return;
    const world = this.world;
    const p = world.fighters[this.playerId], d = world.fighters[this.dummyId];
    if (p && p.grounded && p.grabbedBy === NO_ID && this.recoverAt === NO_TICK) pullInward(world, p);
    // Kukla: tutulurken (konumu tutan belirler) ve düşerken karışma.
    if (d && d.grounded && d.grabbedBy === NO_ID && d.state !== FState.Falling) pullInward(world, d);
  }

  /** Panel senkronu (her tick; DOM'a yalnız değişince yazılır). */
  private syncPanel(tick: number): void {
    this.panel.setProgress(this.runner.progress.done, this.runner.progress.need);
    const hint = this.runner.lesson.hint;
    if (this.runner.hintDue(tick)) {
      if (this.hintShown !== hint) { this.panel.showHint(hint); this.hintShown = hint; }
      else if (this.runner.hintPulse(tick)) this.panel.showHint(hint);   // 12 s: aynı metin → satır yanıp söner
    }
  }

  /* ------------------------------------------------------- koruma kuralları (TUTORIAL §1) */

  /** Kukla: elenmez, düşmez, oyuncuya döner, vurulmadığı sürece tazelenir. */
  private guardDummy(): void {
    const world = this.world;
    const f = world.fighters[this.dummyId];
    if (!f) return;
    // Can hep dolu: `kenara-at` dersinde gerçekten düşüyor (belge istiyor) ama elenip maçı bitirmesin.
    f.lives = T.RESPAWN.lives;
    if (f.state === FState.Eliminated) setState(f, FState.Idle, world.tick);
    // Düşüyorsa sim'in kendi yeniden doğuşu (90 t) beklenir — araya girmek onu yarı yolda dondururdu.
    if (f.state !== FState.Falling) {
      if (this.runner.lesson.id !== NO_EDGE_LOCK_LESSON) keepDummyAlive(world, this.dummyId, this.homeX, this.homeZ);
      // Bakış: yalnız kendi girdisi olmayan modlarda; 'fights' modunda gerçek bot kendi nişanını alır.
      if (this.dummy.mode !== 'fights') faceDummy(world, this.dummyId, this.playerId);
    }
    // 6 s vuruşsuzluk → %0. (lastHitTick NO_TICK ise zaten temiz; resetDummy onu NO_TICK'e çeker → tek sefer çalışır.)
    if (f.lastHitTick !== NO_TICK && dummyUntouchedFor(world, this.dummyId, TUT_MODE.dummyResetTicks)) resetDummy(world, this.dummyId);
  }

  /** Oyuncu: ölüm yok — y < −4'te yakalanır, 30 t sonra en yakın spawn'da geri gelir (can eksilmez, ring-out sunumu olmaz). */
  private guardPlayer(): void {
    const world = this.world, tick = world.tick;
    const p = world.fighters[this.playerId];
    if (!p) return;
    if (this.recoverAt !== NO_TICK) {
      if (tick >= this.recoverAt) this.revivePlayer();
      return;
    }
    if (p.pos.y >= TUT_MODE.catchY || p.state === FState.Eliminated) return;
    // Yakalama: durum Falling → collision.ts kill plane dalı (state !== Falling koşulu) hiç girilmez → Ev.Fell yok.
    this.recoverAt = tick + TUT_MODE.recoverTicks;
    releaseBonds(world, this.playerId);
    dropHeldItems(world, this.playerId);
    p.moveIdx = -1; p.phase = 0; p.blocking = false; p.hitstop = 0;
    p.respawnAt = NO_TICK; p.fellAt = tick;
    p.controlLockUntil = FAR_FUTURE;
    setState(p, FState.Falling, tick);
  }

  /** Geri getirme: en yakın spawn (belge: "en yakın"), can eksilmeden, Ev.Respawn ile (sayaç sıfırlanır, panelde tek satır). */
  private revivePlayer(): void {
    const world = this.world, tick = world.tick;
    const p = world.fighters[this.playerId];
    this.recoverAt = NO_TICK;
    if (!p) return;
    const sp = nearestSpawn(world, p.pos.x, p.pos.z);
    placeFighter(p, sp, yawToCenter(world, sp.x, sp.z), tick);
    setState(p, FState.Respawning, tick, tick + T.RESPAWN.controlAt);
    p.controlLockUntil = tick + T.RESPAWN.controlAt;
    p.spawnProtUntil = tick + T.RESPAWN.protectionTicks;
    p.respawnAt = NO_TICK; p.fellAt = NO_TICK;
    const e = world.events.push(Ev.Respawn, tick, p.id);
    e.x = sp.x; e.y = sp.y; e.z = sp.z;
    // Panel satırı ders yeniden kurulduktan SONRA yazılır (show() ipucu alanını temizler).
    this.pendingNote = 'Düştün, hemen geri geldin. Devam.';
  }

  /* ------------------------------------------------------- kılıç (TUTORIAL §6 kanca 4) */

  /** Silah derslerinde oyuncunun eli ve sahne boşsa bir kılıç doğurur (eğitimde yok olmaz). */
  private upkeepWeapon(): void {
    const world = this.world, tick = world.tick;
    if (!WEAPON_LESSONS.has(this.runner.lesson.id)) return;
    if (tick < this.itemAt) return;
    const p = world.fighters[this.playerId];
    if (!p || p.weapon !== 'unarmed' || p.shieldItem !== NO_ID) return;
    for (let i = 0; i < world.items.length; i++) if (world.items[i]!.state !== 'dead') return;   // sahada kılıç var (yerde/uçuyor/elde)
    this.itemAt = tick + TUT_MODE.itemRetryTicks;
    spawnSword(world, p.pos, tick, world.fighters[this.dummyId]?.pos);
  }
}

/* ------------------------------------------------------------- saf yardımcılar */

/** Kenar sorgusu için scratch (tahsissiz olması gerekmez — soğuk yol — ama gereksiz çöp de üretmesin). */
const _n: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * Kenardan `edgeKeep`ten yakın duran dövüşçüyü merkeze doğru en çok `edgePull` kaydırır (bkz. `gatherToCenter`).
 * NEDEN `placeFighter` değil: o TAM sıfırlama yapar — elindeki kılıcı, hamlesini, doğuş korumasını da siler; burada
 * yalnız konum kayar, durum/hamle/silah aynen sürer. `prevPos` aynı miktarda kaydırılır ki görüntü kayması pürüzsüz olsun.
 * Dönüş: bu tick kaydırıldı mı.
 */
function pullInward(world: SimWorld, f: Fighter): boolean {
  const dist = world.query.edgeDist(f.pos.x, f.pos.z, _n);
  if (!(dist < TUT_MODE.edgeKeep)) return false;                  // NaN de buraya düşer: karışma
  let dx = world.arena.center.x - f.pos.x, dz = world.arena.center.z - f.pos.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-3) return false;
  const step = Math.min(TUT_MODE.edgePull, len);
  dx = (dx / len) * step; dz = (dz / len) * step;
  const x = f.pos.x + dx, z = f.pos.z + dz;
  if (!world.query.isInside(x, z)) return false;
  const gy = world.query.groundY(x, z, f.pos.y + 1);
  if (Number.isNaN(gy) || gy > f.pos.y + 0.05) return false;      // yukarı basamağa itmesin
  f.pos.x = x; f.pos.z = z;
  f.prevPos.x += dx; f.prevPos.z += dz;
  return true;
}

/** Ders verisi → panel görünümü. Metin üretilmez: LESSONS'tan birebir taşınır. */
function lessonView(lesson: TutorialLesson, no: number, total: number): TutorialLessonView {
  const mini = lesson.need <= 0;
  const keys = lesson.keys.kb.length > 0
    ? [{ kb: lesson.keys.kb, pad: lesson.keys.pad }]
    : mini ? FREE_KEYS : [];
  return {
    index: no, total, title: lesson.title, desc: lesson.text,
    goal: lesson.goal === '' ? undefined : lesson.goal,
    keys, need: lesson.need, mini,
  };
}

/** Düşen/geri gelen oyuncunun tutma bağları (iki taraf da temizlenir; placeFighter yalnız kendi alanlarını sıfırlar). */
function releaseBonds(world: SimWorld, id: number): void {
  const f = world.fighters[id];
  if (!f) return;
  if (f.grabTarget !== NO_ID) {
    const v = world.fighters[f.grabTarget];
    if (v && v.grabbedBy === id) { v.grabbedBy = NO_ID; v.holdSince = NO_TICK; if (v.state === FState.Held || v.state === FState.Carried) setState(v, FState.Air, world.tick); }
    f.grabTarget = NO_ID; f.holdSince = NO_TICK; f.carryUntil = NO_TICK;
  }
  if (f.grabbedBy !== NO_ID) {
    const h = world.fighters[f.grabbedBy];
    if (h && h.grabTarget === id) { h.grabTarget = NO_ID; h.holdSince = NO_TICK; h.carryUntil = NO_TICK; setState(h, FState.Idle, world.tick); }
    f.grabbedBy = NO_ID; f.holdSince = NO_TICK;
  }
}

/** Elindeki eşyalar düşenle birlikte yok olur (match.fell ile aynı kural; yetim 'held' eşya kalmasın). */
function dropHeldItems(world: SimWorld, id: number): void {
  for (let i = 0; i < world.items.length; i++) {
    const it = world.items[i]!;
    if (it.state === 'held' && it.holder === id) { it.state = 'dead'; it.holder = NO_ID; }
  }
}

/** En yakın spawn noktası (belge: düşen oyuncu "en yakın spawn"da geri gelir — uzağa atmak dersi böler). */
function nearestSpawn(world: SimWorld, x: number, z: number): Vec3 {
  const sp = world.arena.spawns;
  let best = sp[0] ?? world.arena.center, bd = Infinity;
  for (let i = 0; i < sp.length; i++) {
    const s = sp[i]!;
    const dx = s.x - x, dz = s.z - z, d = dx * dx + dz * dz;
    if (d < bd) { bd = d; best = s; }
  }
  return best;
}

/** Spawn yaw'ı: arena merkezine bakar (match.ts ile aynı). */
function yawToCenter(world: SimWorld, x: number, z: number): number {
  const dx = world.arena.center.x - x, dz = world.arena.center.z - z;
  return dx === 0 && dz === 0 ? 0 : Math.atan2(dx, dz);
}

/**
 * Oyuncunun yanına bir kılıç düşürür (TUTORIAL §6: `sword_1handed`, eğitimde yok olmaz).
 * Nokta arena MERKEZİNE doğru 1.6 u: kenarda duran oyuncuya doğan kılıç aşağı düşmesin.
 * `spawn` türü ağırlıklı prop seçer (1h → kılıç ya da balta); belge kılıç istediği için prop yerinde sabitlenir —
 * doğma olayı aynı tick'te sunulmadan önce yazıldığı için görünüm doğru propu kurar.
 */
export function spawnSword(world: SimWorld, near: Vec3, tick: number, avoid?: Vec3): number {
  const cx = world.arena.center.x, cz = world.arena.center.z;
  const bx = cx - near.x, bz = cz - near.z;
  const base = Math.hypot(bx, bz) < 1e-3 ? 0 : Math.atan2(bx, bz);
  // Merkez yönünden başlayıp 45°'lik adımlarla dönerek ilk UYGUN noktayı seç: platformda, kenardan uzak ve kuklanın
  // tutma menzilinin dışında. Hiçbiri tam uymazsa kuklaya en uzak aday kullanılır.
  let x = near.x, z = near.z, best = -Infinity;
  for (let k = 0; k < 8; k++) {
    const a = base + k * (Math.PI / 4);
    const px = near.x + Math.sin(a) * TUT_MODE.itemDist, pz = near.z + Math.cos(a) * TUT_MODE.itemDist;
    if (!world.query.isInside(px, pz) || world.query.edgeDist(px, pz, _n) < TUT_MODE.itemEdgeKeep) continue;
    const away = avoid ? Math.hypot(px - avoid.x, pz - avoid.z) : Infinity;
    const score = Math.min(away, TUT_MODE.itemAvoid) - k * 0.01;   // eşitlikte merkez yönü kazansın
    if (score > best) { best = score; x = px; z = pz; }
    if (away >= TUT_MODE.itemAvoid) break;
  }
  const gy = world.query.groundY(x, z, near.y + 2);
  const at = { x, y: Number.isNaN(gy) ? 0 : gy, z };
  const id = world.systems.items.spawn(world, '1h', at, tick);
  const it = id >= 0 ? world.items[id] : undefined;
  if (!it) return NO_ID;
  it.propId = SWORD;
  it.length = T.ITEMS.length[SWORD] ?? 0;
  it.holderOffsetY = T.ITEMS.holderOffsetY[SWORD] ?? 0;
  it.despawnAt = NO_TICK;    // eğitimde 20 s'de yok olmaz (belge)
  return id;
}

/** Eğitim kadrosu için kukla botu (game.ts kurar). */
export const createTutorialDummy = (fighterId: number, seed: number): DummyBot => createDummy(fighterId, LESSONS[0]!.dummy, seed);
